import React, { useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Badge,
  Typography,
  ClickAwayListener,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import { UserAvatar } from '@billda/shared-ui';
import { useShallow } from 'zustand/react/shallow';
import { UserMenuPopover } from '../../../features/auth/components/UserMenuPopover';
import { useCurrentUser } from '../../../features/auth/hooks/useCurrentUser';
import { useShellStore } from '../../stores/shellStore';
import { useUploadTrayStore } from '../../../features/sites/stores/uploadTrayStore';
import { UploadTrayItem } from '../../../features/sites/components/files/UploadTrayItem';
import { useLocation } from 'react-router-dom';
import { findActiveNavItem } from './navigationItems';
import { DARK_SHELL_SURFACE, LIGHT_SHELL_SURFACE } from '@billda/shared-ui';

export const APPBAR_HEIGHT = 52;

const UploadIndicator: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const items = useUploadTrayStore(state => state.items);
  const isExpanded = useUploadTrayStore(state => state.isExpanded);
  const expand = useUploadTrayStore(state => state.expand);
  const minimize = useUploadTrayStore(state => state.minimize);
  const dismiss = useUploadTrayStore(state => state.dismiss);
  const retryItem = useUploadTrayStore(state => state.retryItem);
  const removeItem = useUploadTrayStore(state => state.removeItem);
  const resolveCollision = useUploadTrayStore(state => state.resolveCollision);
  const anchorRef = useRef<HTMLButtonElement>(null);

  if (items.length === 0) {
    return null;
  }

  const activeCount = items.filter(
    i =>
      i.status === 'queued' ||
      i.status === 'requesting-url' ||
      i.status === 'uploading' ||
      i.status === 'confirming'
  ).length;
  const failedCount = items.filter(i => i.status === 'failed').length;
  const completedCount = items.filter(i => i.status === 'completed').length;
  const isUploading = activeCount > 0;

  const handleClick = () => {
    if (isExpanded) {
      minimize();
    } else {
      expand();
    }
  };

  const headerText = isUploading
    ? `Uploading ${activeCount} file${activeCount !== 1 ? 's' : ''}`
    : `${completedCount} file${completedCount !== 1 ? 's' : ''} uploaded`;

  const icon = isUploading ? (
    <CircularProgress size={isMobile ? 18 : 14} color="inherit" />
  ) : failedCount > 0 ? (
    <ErrorOutlineIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
  ) : (
    <UploadFileIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
  );

  const buttonSx = {
    color: failedCount > 0 ? 'error.main' : 'text.secondary',
    '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
  };

  const label = isUploading
    ? `Uploading ${activeCount}`
    : failedCount > 0
      ? `${failedCount} failed`
      : `${completedCount} uploaded`;

  return (
    <>
      {isMobile ? (
        <Tooltip title={label}>
          <IconButton
            ref={anchorRef}
            data-testid="upload-indicator"
            size="small"
            onClick={handleClick}
            sx={buttonSx}
          >
            <Badge
              badgeContent={activeCount || failedCount || null}
              color={failedCount > 0 ? 'error' : 'primary'}
            >
              {icon}
            </Badge>
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          ref={anchorRef}
          data-testid="upload-indicator"
          size="small"
          onClick={handleClick}
          startIcon={icon}
          sx={{
            ...buttonSx,
            textTransform: 'none',
            fontSize: '0.75rem',
          }}
        >
          {label}
        </Button>
      )}

      <Popper
        open={isExpanded && !!anchorRef.current}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        sx={{ zIndex: theme => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={minimize}>
          <Paper
            elevation={8}
            sx={{ borderRadius: 2, overflow: 'hidden', width: 360, mt: 1 }}
            data-testid="upload-tray"
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                bgcolor: 'action.hover',
              }}
            >
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                {headerText}
              </Typography>
              <IconButton size="small" onClick={dismiss}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {items.map(item => (
                <UploadTrayItem
                  key={item.localId}
                  item={item}
                  onRetry={retryItem}
                  onRemove={removeItem}
                  onResolveCollision={resolveCollision}
                />
              ))}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export const TopAppBar: React.FC = () => {
  const currentUser = useCurrentUser();
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { toggleSidebar, sidebarCollapsed, toggleDesktopSidebar } =
    useShellStore(
      useShallow(state => ({
        toggleSidebar: state.toggleSidebar,
        sidebarCollapsed: state.sidebarCollapsed,
        toggleDesktopSidebar: state.toggleDesktopSidebar,
      }))
    );

  const location = useLocation();
  const activeItem = findActiveNavItem(location.pathname);

  const handleUserMenuOpen = () => {
    const avatarElement = document.querySelector('[data-testid="user-avatar"]');
    setUserMenuAnchorEl(avatarElement as HTMLElement | null);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const ActiveIcon = activeItem?.icon;

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: theme =>
            theme.palette.mode === 'dark'
              ? DARK_SHELL_SURFACE
              : LIGHT_SHELL_SURFACE,
          height: APPBAR_HEIGHT,
        }}
      >
        <Toolbar
          sx={{
            height: APPBAR_HEIGHT,
            minHeight: `${APPBAR_HEIGHT}px !important`,
            px: 2,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Left: Sidebar toggle — hamburger on mobile, sidebar icon on desktop when collapsed */}
          {isMobile ? (
            <Tooltip title="Menu" placement="bottom">
              <IconButton
                onClick={toggleSidebar}
                data-testid="sidebar-toggle-button"
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : sidebarCollapsed ? (
            <Tooltip title="Open sidebar" placement="bottom">
              <IconButton
                onClick={toggleDesktopSidebar}
                data-testid="sidebar-toggle-button"
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ViewSidebarIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : null}

          {/* Center: Current page title with icon — hidden on mobile portrait */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {ActiveIcon && (
              <ActiveIcon
                sx={{ fontSize: 16, color: 'text.secondary', mr: 0.75 }}
              />
            )}
            {activeItem && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                {activeItem.text}
              </Typography>
            )}
          </Box>

          {/* Right: Actions */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              ml: 'auto',
              zIndex: 1,
            }}
          >
            {/* Upload Indicator */}
            <UploadIndicator isMobile={isMobile} />

            {/* Notifications Icon (Placeholder) */}
            <Tooltip title="Notifications" placement="bottom">
              <IconButton
                data-testid="appbar-notifications-button"
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Badge badgeContent={0} color="primary">
                  <NotificationsIcon sx={{ fontSize: 18 }} />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Avatar */}
            <UserAvatar
              initials={currentUser?.initials || undefined}
              loading={!currentUser}
              fallbackIcon={<PersonIcon sx={{ fontSize: 16 }} />}
              onClick={handleUserMenuOpen}
              tooltip="Profile"
              size="small"
              border={true}
              testId="user-avatar"
            />
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu Popover */}
      <UserMenuPopover
        anchorEl={userMenuAnchorEl}
        onClose={handleUserMenuClose}
      />
    </>
  );
};
